import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

// Jedno globalne źródło prawdy o polubieniach zalogowanego użytkownika.
// Wszystkie karty (strona główna, Najpopularniejsze, Przeglądaj, Na sprzedaż,
// profil, szczegóły koszulki) czytają stąd, więc serca są zawsze spójne.
export const myLikesKey = (userId?: string | null) => ["my-likes", userId ?? "guest"];

export function useMyLikes() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: myLikesKey(user?.id),
    enabled: !!user,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from("kit_likes").select("kit_id").eq("user_id", user!.id);
      if (error) throw error;
      return (data || []).map((row: any) => row.kit_id as string);
    },
  });

  const likedIds = useMemo(() => new Set<string>(data || []), [data]);
  const ready = !!user && Array.isArray(data);

  const isLiked = useCallback(
    (kitId: string, fallback = false) => (ready ? likedIds.has(kitId) : fallback),
    [ready, likedIds],
  );

  const setLike = useCallback(
    (kitId: string, liked: boolean) => {
      if (!user) return;
      queryClient.setQueryData(myLikesKey(user.id), (prev: string[] | undefined) => {
        const current = prev || [];
        if (liked) return current.includes(kitId) ? current : [...current, kitId];
        return current.filter((id) => id !== kitId);
      });
    },
    [queryClient, user?.id],
  );

  return { likedIds, isLiked, setLike, ready };
}
